import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import {
	Autocomplete,
	Box,
	Checkbox,
	Chip,
	createFilterOptions,
	Typography,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { objType } from "data/globalData";
import { inputTypes } from "data/globalData";
import { useCallback, useMemo } from "react";

const LISTBOX_MAX_HEIGHT = 280;
const OPTION_RENDER_LIMIT = 150;

const SELECT_ALL = {
	key: "__select_all__",
	label: "Select All",
	name: "Select All",
};

const baseFilter = createFilterOptions({
	matchFrom: "any",
	stringify: (option) => `${option?.label ?? ""} ${option?.key ?? ""}`,
	limit: OPTION_RENDER_LIMIT,
});

const SelectInputField = ({
	data,
	setData,
	column,
	width,
	options,
	sx,
	onChange,
	disabled,
	required,
	disabledOptionFun,
	disableLabel = false,
}) => {
	const sxData = sx ? sx : {};
	let { type, list, getOptionLabel } = options || {};

	if (type === objType) list = Object.keys(list || {})?.map((key) => list[key]);
	let optionsList = Array.isArray(list) ? [...list] : [];

	const multiple = column.input_type === inputTypes.MULTIPLE_SELECT.key;
	const selectedValue = data?.[column.key] ?? (multiple ? [] : null);

	if (typeof getOptionLabel === "function") {
		optionsList = (Array.isArray(list) ? list : []).map((row) => {
			const { name, key } = getOptionLabel(row);
			return {
				name,
				key,
				label: name,
			};
		});
	} else {
		optionsList = optionsList.map((row) => ({
			...row,
			label: row?.name ?? row?.label,
		}));
	}

	const allOptionKeys = useMemo(
		() => optionsList.map((o) => o.key),
		[optionsList]
	);

	const withSelectAll = multiple ? [SELECT_ALL, ...optionsList] : optionsList;

	const isAllSelected = multiple && selectedValue?.length === optionsList.length;
	const selectedCount = Array.isArray(selectedValue) ? selectedValue.length : 0;

	const filterOptions = useCallback((opts, state) => {
		const hasSelectAll = opts[0]?.key === SELECT_ALL.key;
		const selectable = hasSelectAll ? opts.slice(1) : opts;
		const filtered = baseFilter(selectable, state);
		return hasSelectAll ? [opts[0], ...filtered] : filtered;
	}, []);

	const handleChange = (event, value) => {
		if (!multiple) {
			const selected = value?.key ?? null;
			if (typeof onChange === "function") {
				onChange({ target: { value: selected } });
			} else {
				setData({ ...data, [column.key]: selected });
			}
			return;
		}

		const hasSelectAll = value[value.length - 1]?.key === SELECT_ALL.key;
		const selectedKeys = value
			.filter((v) => v.key !== SELECT_ALL.key)
			.map((v) => v.key);

		const finalKeys = hasSelectAll
			? isAllSelected
				? []
				: allOptionKeys
			: selectedKeys;

		if (typeof onChange === "function") {
			onChange({ target: { value: finalKeys } });
		} else {
			setData({ ...data, [column.key]: finalKeys });
		}
	};

	const memoizedDisabledFun = useCallback(
		(col, rowData, option) => {
			return typeof disabledOptionFun === "function"
				? disabledOptionFun(col, rowData, option)
				: false;
		},
		[disabledOptionFun]
	);

	const selectedOptions = multiple
		? optionsList.filter((opt) => selectedValue?.includes(opt.key))
		: optionsList.find((opt) => opt.key === selectedValue) ?? null;

	return (
		<Stack
			sx={{ width, minWidth: 0, ...sxData }}
			spacing={0.5}
			minHeight={disableLabel ? "auto" : 72}>
			{!disableLabel && (
				<InputLabel
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: grey[700],
						fontSize: "0.75rem",
						fontWeight: 600,
						letterSpacing: "0.02em",
						textTransform: "uppercase",
					}}>
					{column?.name}
					{required && <span style={{ color: "#d32f2f" }}> *</span>}
				</InputLabel>
			)}

			<Autocomplete
				disableCloseOnSelect={multiple}
				disabled={disabled}
				fullWidth
				size="small"
				multiple={multiple}
				options={withSelectAll}
				filterOptions={filterOptions}
				getOptionLabel={(option) => option?.label ?? ""}
				getOptionDisabled={(option) =>
					option.key !== SELECT_ALL.key && memoizedDisabledFun(column, data, option)
				}
				value={selectedOptions}
				isOptionEqualToValue={(opt, val) => opt?.key === val?.key}
				onChange={handleChange}
				ListboxProps={{
					style: {
						maxHeight: LISTBOX_MAX_HEIGHT,
						overflow: "auto",
						paddingTop: 4,
						paddingBottom: 4,
					},
				}}
				componentsProps={{
					paper: {
						elevation: 8,
						sx: {
							mt: 0.5,
							borderRadius: 2,
							border: "1px solid",
							borderColor: "divider",
						},
					},
					popper: {
						placement: "bottom-start",
						modifiers: [
							{ name: "flip", enabled: true },
							{
								name: "preventOverflow",
								enabled: true,
								options: { altAxis: true, tether: false },
							},
						],
					},
				}}
				renderTags={(value) => (
					<Chip
						size="small"
						label={
							value.length === optionsList.length && optionsList.length > 0
								? `All (${value.length})`
								: `${value.length} selected`
						}
						sx={{
							height: 24,
							maxWidth: "70%",
							borderRadius: "6px",
							bgcolor: grey[100],
							fontWeight: 600,
							"& .MuiChip-label": {
								px: 1,
								overflow: "hidden",
								textOverflow: "ellipsis",
							},
						}}
					/>
				)}
				sx={{
					"& .MuiOutlinedInput-root": {
						minHeight: 40,
						borderRadius: 1.5,
						bgcolor: "grey.50",
						alignItems: "center",
						flexWrap: "nowrap",
						pr: 1,
						transition: "background-color 0.15s ease, box-shadow 0.15s ease",
						"&:hover": { bgcolor: "common.white" },
						"&.Mui-focused": {
							bgcolor: "common.white",
							boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.12)",
						},
						"& fieldset": { borderColor: "divider" },
					},
					"& .MuiAutocomplete-inputRoot": {
						display: "flex",
						alignItems: "center",
						flexWrap: "nowrap",
						overflow: "hidden",
						py: "2px !important",
					},
					"& .MuiAutocomplete-tag": {
						maxWidth: "72%",
						margin: "0 4px 0 0",
					},
					"& .MuiAutocomplete-input": {
						minWidth: "48px !important",
						padding: "2px 0 !important",
					},
					"& .MuiAutocomplete-endAdornment": {
						right: 6,
					},
				}}
				renderOption={(props, option, { selected }) => {
					const { key, ...optionProps } = props;
					const isSelectAll = option.key === SELECT_ALL.key;
					const isChecked = isSelectAll ? isAllSelected : selected;
					const isIndeterminate =
						isSelectAll && selectedCount > 0 && selectedCount < optionsList.length;

					return (
						<Box
							component="li"
							key={key}
							{...optionProps}
							sx={{
								px: "10px !important",
								py: "6px !important",
								mx: 0.5,
								my: 0.25,
								borderRadius: 1,
								gap: 0.5,
							}}>
							{multiple && (
								<Checkbox
									checked={isChecked}
									indeterminate={isIndeterminate}
									size="small"
									sx={{ margin: 0, padding: "2px", mr: 0.75 }}
									disableRipple
								/>
							)}
							<Typography
								variant="body2"
								fontWeight={isSelectAll ? 700 : 400}
								color={isSelectAll ? green[700] : "text.primary"}
								noWrap
								title={option.label}>
								{option.label}
							</Typography>
						</Box>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={
							selectedCount > 0
								? "Search…"
								: `Search ${column?.name ?? "options"}`
						}
						variant="outlined"
					/>
				)}
				noOptionsText={
					<Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
						No matches
					</Typography>
				}
			/>
		</Stack>
	);
};

export default SelectInputField;
